'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMouse } from '@/hooks/useMouse';
import { cn } from '@/lib/utils';
import NumberFlow from '@number-flow/react';
import { ArrowUpRightIcon } from 'lucide-react';

export const MainMenusGradientCard = ({
  title,
  description,
  withArrow = false,
  circleSize = 400,
  className,
  children,
  borderColor,
  value,
  prefix,
  suffix,
}: {
  title: string;
  description: string;
  withArrow?: boolean;
  circleSize?: number;
  children?: ReactNode;
  className?: string;
  borderColor?: string;
  value: number;
  prefix?: string;
  suffix?: string;
}) => {
  const [mouse, parentRef] = useMouse();
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<any>(null);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setVal(value);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className='group relative transform-gpu overflow-hidden rounded-[10px] sm:rounded-[20px] bg-[#082a4c6c] p-2 transition-transform hover:scale-[1.01] active:scale-90 sm:min-h-56 flex flex-col justify-center items-center w-full flex-1'
      ref={el => {
        // Combine both refs
        if (el) {
          (parentRef as any).current = el;
          cardRef.current = el;
        }
      }}
    >
      {withArrow && (
        <ArrowUpRightIcon className='absolute right-2 top-2 z-10 size-5 translate-y-4 opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100 text-neutral-300' />
      )}
      <div
        className={cn(
          'absolute -translate-x-1/2 -translate-y-1/2 transform-gpu rounded-full transition-transform duration-500 group-hover:scale-[3]',
          mouse.elementX === null || mouse.elementY === null ? 'opacity-0' : 'opacity-100'
        )}
        style={{
          maskImage: `radial-gradient(${circleSize / 2}px circle at center, white, transparent)`,
          width: `${circleSize}px`,
          height: `${circleSize}px`,
          left: `${mouse.elementX}px`,
          top: `${mouse.elementY}px`,
          background: `linear-gradient(135deg, #${borderColor}, #${borderColor},#${borderColor},#${borderColor})`,
        }}
      />
      <div className='absolute inset-px rounded-[10px] sm:rounded-[19px] bg-neutral-900/80' />
      <div className='relative px-4 pb-2 pt-4 flex flex-col justify-center items-center h-full'>
        {/* <h3 className={`title text-[#${borderColor}]`}>{title}</h3> */}
        <NumberFlow
          value={val}
          className={`title text-[#${borderColor}]`}
          prefix={prefix}
          suffix={suffix}
          spinTiming={{ duration: 1000, easing: 'ease-in' }}
        />
        <p className='description'>{description}</p>
      </div>
      <BorderBar className={`via-[#${borderColor}]`} />
    </div>
  );
};

const BorderBar = ({ className }: { className: string }) => {
  return (
    <div
      className={cn(
        'absolute top-0 z-10 h-[1px] bg-gradient-to-r from-transparent to-transparent w-full',
        className
      )}
    />
  );
};
